import { Component, Input, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { BehaviorSubject, catchError, combineLatest, filter, map, Observable, of, startWith, Subject, switchMap, take, tap, withLatestFrom, } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { IBaseGetByComanyRequest, IFilter, IGridState, PageSize } from 'src/app/core/models/base';
import { ICompany } from 'src/app/company/models/company';
import { DeviceStatusEnum } from '../models/device';
import * as DeviceActions from "../../stores/global/devices/device-actions";
import * as EmployeeActions from "../../stores/global/employees/employee-actions";
import * as DeviceCategoryActions from "../../stores/global/device-categories/actions";
import * as DeviceLocationsActions from "../../stores/global/device-locations/device-locations-actions";
import * as DeviceManufacturerActions from "../../stores/global/device-manufacturers/device-manufacturers-actions";
import * as InstallationsActions from "../../stores/global/installations/installation-actions";
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { deviceCategoriesSelector } from 'src/app/stores/global/device-categories/selectors';
import { deviceLocationSelector } from 'src/app/stores/global/device-locations/device-location-selectors';
import { deviceManufacturerSelector } from 'src/app/stores/global/device-manufacturers/device-manufacturers-selectors';
import { DeviceCategoryStore } from 'src/app/stores/component/device-category-store';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';
import { DeviceService } from '../services/device.service';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';
import { installationsSelector } from 'src/app/stores/global/installations/installation-selectors';
import { IGetInstallationsByCompanyRequest } from 'src/app/installations/models/installation';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { endOfLocalDay } from 'src/app/shared/helpers/formatDates';


@UntilDestroy()
@Component({
    selector: 'app-device-view-client',
    templateUrl: './device-view-client.component.html',
    styleUrl: './device-view-client.component.scss',
    providers: [DeviceCategoryStore],
    standalone: false
})
export class DeviceViewClientComponent implements OnInit {

  ////////////////
  // variables //
  //////////////

  @Input() showActionCol : boolean = true;
  @Input() showOrderColumn : boolean = true;
  @Input() createProducts : boolean = true;
  @Input() reloadResults : boolean = true;
  @Input() loadAllProducts : boolean = true;
  @Input() isAdminView : boolean = false;
  @Input() currentCompany: ICompany = null;

  vm$: Observable<any>;
  keyword : string;
  showReset = false;
  companyId = null;
  loading: boolean = false;


  // Grid Form
  public formGroup = new UntypedFormGroup({
    manufacturerId: new UntypedFormControl(''),
    serialNumber: new UntypedFormControl(''),
    deviceStatus: new UntypedFormControl(''),
  });

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: "contains",
  };

  // Create filter form
  filterForm = this.fb.group({
    keyword: new FormControl(null),
    deviceCategoryId: new UntypedFormControl(null),
    deviceManufacturerId: new UntypedFormControl(null),
    installationId: new UntypedFormControl(null),
    deviceStatus: new UntypedFormControl(''),
    deviceLocationId: new UntypedFormControl(null),
    executionDateFrom: new UntypedFormControl(null),
    executionDateTo: new UntypedFormControl(null),
    productKeyword: ['']
  });

  deviceStatusArray = enumToArray(DeviceStatusEnum, "global.deviceStatusEnums.");

  constructor(
    private fb : UntypedFormBuilder,
    private store: Store<AppStateInterface>,
    private deviceCategoryStore: DeviceCategoryStore,
    private route: ActivatedRoute,
    private router: Router,
    private deviceService: DeviceService
  ) {
    this.vm$ = combineLatest([
      this.gridState$.pipe(),
      this.store.pipe(select(currentEmployeeSelector)).pipe(tap((employee) => {
        if (employee?.company?.id) {
          this.companyId = employee.company.id;
      
          // Update the search request with the new companyId
          this.searchRequest$.next({
            ...this.searchRequest$.value,
            companyId: this.companyId,
          });

          // load locations and installations according to company
          this.store.dispatch(DeviceLocationsActions.getDeviceLocationsByCompany({companyId: this.companyId}));

          var request : IGetInstallationsByCompanyRequest = {
            companyId: this.companyId
          };
          this.store.dispatch(InstallationsActions.getInstallationsByCompany({request}))
        }
      })),
      this.searchRequest$.pipe(
        filter(() => this.companyId != null),
        switchMap((request) => {
          this.loading = true;
          const updatedRequest = { ...request, companyId: this.companyId };
      
          return this.deviceService.getDevicesByCompany(updatedRequest)
            .pipe(
              catchError(() => of({ data: [], totalCount: 0 }))
            );
        }),
        tap(() => {
          this.loading = false;
        }),
        map(x => {
          return {
            data: x?.data ?? [],
            total: x?.totalCount ?? 0
          };
        }),
        startWith({ data: [], total: 0 })
      ),
      this.searchRequest$.pipe(startWith(null)),
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
        })),
        tap((x) => {
          const pageNumber = Math.round(
            (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
              x.pageChangeEvent.take
          );
   
          this.gridState$.next({
            skip: x.pageChangeEvent.skip,
            pageSize: x.pageChangeEvent.take,
          });
   
          this.searchRequest$.next({
            ...x.searchRequest,
            pageNumber: pageNumber,
            pageSize: x.pageChangeEvent.take,
          });
        }),
        startWith({ skip: 0, take: 10 })
      ),
      this.store.pipe(select(deviceCategoriesSelector)),
      this.store.pipe(select(deviceLocationSelector)),
      this.store.pipe(select(deviceManufacturerSelector)),
      this.store.pipe(select(installationsSelector)),
      this.deviceCategoryStore.selectedCategory$,
      this.route.queryParams.pipe(
        tap(params => this.patchFilterForm(params)),
        withLatestFrom(this.searchRequest$),
        map(([params, searchRequest]) => ({params, searchRequest})),
        tap(data => {
          if(JSON.stringify(data.params) !== "{}")
          {
            this.onSubmit(data.searchRequest)
          }
        }),
        take(1)
      )
    ]).pipe(
      map(
        ([
          gridState,
          currentEmployee,
          devices,
          searchRequest,
          pageChangeSub,
          deviceCategories,
          deviceLocations,
          deviceManufacturers,
          installations,
          deviceCategory,
          filterForm   
        ]) => ({
          gridState,
          currentEmployee,
          devices,
          searchRequest,
          pageChangeSub,
          deviceCategories,
          deviceLocations,
          deviceManufacturers,
          installations,
          deviceCategory,
          filterForm   
        })
      )
    );
  }

  ngOnInit(): void {
    // get data from stores
    this.store.dispatch(EmployeeActions.getCurrentEmployee());
    this.store.dispatch(DeviceCategoryActions.getDeviceCategories());
    this.store.dispatch(DeviceManufacturerActions.getDeviceManufacturers());
  }

  ///////////////
  // Getters  //
  /////////////

  get pageSizes() {
    return PageSize;
  }

  get deviceStatus(){
    return DeviceStatusEnum;
  }

  getControl(path: string) {
    return this.filterForm.get(path) as FormControl;
  }

  ////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBaseGetByComanyRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    companyId: null
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();

  ////////////////
  // functions //
  //////////////

  patchFilterForm(params: any) {
    this.filterForm.patchValue({
      keyword: params['keyword'] || null,
      deviceManufacturerId: params['deviceManufacturerId'] || null,
      installationId: params['installationId'] || null,
      deviceStatus: parseInt(params['deviceStatus']) || null,
      deviceCategoryId: params['deviceCategoryId'] || null,
      deviceLocationId: params['deviceLocationId'] || null,
      executionDateFrom: params['executionDateFrom'] != null ? new Date(params['executionDateFrom']) : null,
      executionDateTo: params['executionDateTo'] != null ? new Date(params['executionDateTo']) : null,
      productKeyword: params['productKeyword'] || null
    });
  }

  onSubmit(request: IBaseGetByComanyRequest): void {
    var formValues = this.filterForm.value;

    const searchRequest: IBaseGetByComanyRequest = {
      ...request,
      advancedFilter: undefined,
      productKeyword: undefined,
      scheduledDateFrom: undefined,
      scheduledDateTo: undefined,
    };

    // Trim the keyword before using
    const keyword = formValues.keyword ? formValues.keyword.trim() : null;

    // Fields to search for the keyword
    const searchFields = ['serialNumber', 'tagNumber', 'equipmentNumber', 'type', 'qrNumber'];

    // Create keyword filters
    const keywordFilters: IFilter[] = keyword 
        ? searchFields.map(field => ({ field, operator: 'contains', value: keyword })) 
        : [];

    // Other filters
    var filterObject : IFilter = {
      logic: "and",
      filters: []
    }

    var otherFilters: IFilter[] =[]

    if(formValues.deviceLocationId) {
      otherFilters.push({ field: 'deviceLocation.id', operator: 'eq', value: formValues.deviceLocationId })
    }

    if(formValues.installationId) {
      otherFilters.push({ field: 'installation.id', operator: 'eq', value: formValues.installationId })
    }

    if(formValues.deviceManufacturerId) {
      otherFilters.push({ field: 'manufacturer.id', operator: 'eq', value: formValues.deviceManufacturerId })
    }

    if(formValues.deviceCategoryId) {
      otherFilters.push({ field: 'deviceCategory.id', operator: 'eq', value: formValues.deviceCategoryId })
    }

    if(formValues.deviceStatus) {
      otherFilters.push({ field: 'deviceStatus', operator: 'eq', value: formValues.deviceStatus ? formValues.deviceStatus.toString() : null })
    }

    // Product keyword search - passed as dedicated property
    if(formValues.productKeyword && formValues.productKeyword.trim()) {
      searchRequest.productKeyword = formValues.productKeyword.trim();
    }

    // Combine keyword filters with 'or' logic if there are any
    const combinedKeywordFilter: IFilter = { logic: 'or', filters: keywordFilters };

    // Combine keyword filters with other filters using 'and' logic
    const allFilters: IFilter[] = combinedKeywordFilter.filters.length > 0
        ? [combinedKeywordFilter, ...otherFilters]
        : otherFilters;

    if(formValues.executionDateFrom){
      formValues.executionDateFrom = new Date(formValues.executionDateFrom).toISOString();
      searchRequest.scheduledDateFrom = formValues.executionDateFrom;
    }
    if(formValues.executionDateTo){
      formValues.executionDateTo = new Date(formValues.executionDateTo).toISOString();
      searchRequest.scheduledDateTo = endOfLocalDay(formValues.executionDateTo);
    }

    filterObject.filters = allFilters;

    if(filterObject.filters.length > 0){
      searchRequest.advancedFilter = filterObject;
    }
    this.searchRequest$.next(searchRequest);

    this.updateUrlWithQuery(formValues);
  }

  updateUrlWithQuery(formValues){
    // Update URL with the filter parameters
    const queryParams = {
      keyword: formValues.keyword || null,
      deviceCategoryId: formValues.deviceCategoryId || null,
      deviceManufacturerId: formValues.deviceManufacturerId || null,
      installationId: formValues.installationId || null,
      deviceStatus: formValues.deviceStatus || null,
      deviceLocationId: formValues.deviceLocationId || null,
      executionDateFrom: formValues.executionDateFrom || null,
      executionDateTo: formValues.executionDateTo || null,
      productKeyword: formValues.productKeyword || null
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  resetFilter(): void {
    // reset form
    this.filterForm.reset();

    this.searchRequest$.next({pageNumber: 0,pageSize: this.pageSizes.Small, companyId: this.companyId})
    this.gridState$.next({ pageSize: PageSize.Small, skip: 0,})

    // reset url params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: '',
    });
  }
}

function checkProperties(obj: Record<string, any>): boolean {
  return Object.values(obj).every(value => value === null);
}
